import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { error, debug } from '@/services/Log'
import { FACEBOOK_PIXEL_ID, OAUTH_API } from '@/services/Configuration'

// Pixel is the front-end APi that sends data to Meta, Docs:
// https://developers.facebook.com/docs/meta-pixel/reference

const hashing = async (data?: string) => {
  if (typeof data === 'string') {
    const [{ default: { sha256 } }] = await allPromisesWithRetries(() => [import('js-sha256')])
    return sha256(data.trim().toLowerCase())
  }
  return null
}

const parseUserData = async (email?: string, phone?: string) => {
  const [{ default: Cookies }] = await allPromisesWithRetries(() => [import('js-cookie')])
  return ({
    fbc: Cookies.get('_fbc'),
    fbp: Cookies.get('_fbp'),
    em: await hashing(email),
    ph: await hashing(phone),
    client_user_agent: navigator?.userAgent || '',
  })
}

export type FacebookInitFunction = (operation: 'init', pixelId: string) => Promise<unknown> | unknown
export type FacebookStandardEvent = 'AddPaymentInfo' | 'AddToCart' | 'AddToWishlist' | 'CompleteRegistration' | 'Contact' | 'CustomizeProduct' | 'Donate' | 'FindLocation' | 'InitiateCheckout' | 'Lead' | 'Purchase' | 'Schedule' | 'Search' | 'StartTrial' | 'SubmitApplication' | 'Subscribe' | 'ViewContent' | 'PageView'

export type FacebookPixelContentType = 'product' | 'product_group'
export type FacebookPixelContentIds = Array<number | string>
export type FacebookPixelContents = Array<{
  id: string,
  quantity: number | null,
  item_price: number | null,
}>

export type FacebookEventPayload = {
  content_category?: string
  content_ids?: FacebookPixelContentIds
  content_name?: string
  content_type?: FacebookPixelContentType
  contents?: FacebookPixelContents
  currency?: string
  order_id?: string,
  num_items?: number
  predicted_ltv?: number
  search_string?: string
  status?: boolean
  value?: number,
  user_email?: string,
}

export type FacebookEventId = { eventID: string }
export type FacebookEventFunction = (operation: 'track', eventName: FacebookStandardEvent, eventPayload?: FacebookEventPayload, eventId?: FacebookEventId) => Promise<unknown> | unknown

type CoversionAPIPayload<T = undefined> = {
  user?: UserData | null,
  eventID?: string
} & T

type PixelSafeguardHandler = (
  eventName: FacebookStandardEvent,
  eventPayload?: CoversionAPIPayload<FacebookEventPayload>,
  id?: { eventID: string }
) => Promise<{ success: boolean }>
const Pixel: PixelSafeguardHandler = async (eventName, eventPayload = undefined, { eventID } = { eventID: '' }) => {
  debug(`Facebook: Pixel invoked for ${eventName}`)
  if (typeof window !== 'object') {
    debug('Facebook: error no Window')
    return { success: false }
  }
  const [{ v4: uuidv4 }] = await allPromisesWithRetries(() => [import('uuid')])
  const event_id = eventID || uuidv4()
  const user: UserData | null = eventPayload?.user || null
  try {
    if (eventPayload && user) {
      delete eventPayload.user
    }
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      debug('Facebook: Window.FBQ Called')
      await window.fbq('track', eventName, eventPayload, { eventID: event_id })
    } else {
      error('Facebook: Either window is not an object or fbq is not a function')
    }
    if (typeof eventPayload === 'object') {
      const event_time = Math.floor((Date.now() / 1000))
      const source_url = window.location.href
      const userdata = await parseUserData(user?.email, user?.phone)
      const [{ default: Axios }] = await allPromisesWithRetries(() => [import('axios')])
      debug('Facebook: Conversion API Request Sent', eventPayload)
      await Axios.post(`${OAUTH_API}/sf/marketing/facebookEvent/${eventName}`, {
        ...eventPayload,
        ...userdata,
        event_time,
        source_url,
        event_id,
      })
    }
    return { success: true }
  } catch (err) {
    error(`Facebook: Pixel ${eventName} Failed ${String(err)}`, err)
  }
  return { success: false }
}
type UserData = { email: string, phone: string }
type FacebookAddToCartPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number
}>
type AddToCartHandler = (
  item: FacebookAddToCartPayload
) => Promise<{ success: boolean }> | { success: boolean }
const addToCart: AddToCartHandler = ({
  content_ids,
  content_category,
  content_name,
  content_type = 'product',
  currency = 'USD',
  contents,
  value,
  user,
}) => Pixel('AddToCart', {
  content_ids,
  content_category,
  content_name,
  content_type,
  contents,
  currency,
  value,
  user,
})

type FacebookViewContentPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number
}>

type ViewContentHandler = (
  item: FacebookViewContentPayload
) => Promise<{ success: boolean }>
const viewContent: ViewContentHandler = ({
  content_ids,
  content_category,
  content_name,
  content_type = 'product',
  currency = 'USD',
  contents,
  value,
  user,
}) => Pixel('ViewContent', {
  content_ids,
  content_category,
  content_name,
  content_type,
  currency,
  contents,
  value,
  user,
})

type FacebookInitializeCheckoutPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  contents: FacebookPixelContents,
  content_type?: FacebookPixelContentType,
  currency?: string,
  value: number,
  num_items: number,
}>

type InitializeCheckoutHandler = (
  item: FacebookInitializeCheckoutPayload
) => Promise<{ success: boolean }> | { success: boolean }
const initializeCheckout: InitializeCheckoutHandler = ({
  content_ids,
  contents,
  content_type = 'product',
  currency = 'USD',
  value,
  num_items,
  user,
}) => Pixel('InitiateCheckout', {
  content_ids,
  contents,
  content_type,
  currency,
  num_items,
  value,
  user,
})

type TrackPurchasePayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  order_id: string,
  content_type?: FacebookPixelContentType,
  contents: FacebookPixelContents,
  currency?: string,
  value: number,
  num_items: number,
}>

type TrackPurchaseHandler = (
  item: TrackPurchasePayload
) => Promise<{ success: boolean }> | { success: boolean }
const trackPurchase: TrackPurchaseHandler = ({
  content_ids,
  order_id,
  content_type = 'product',
  contents,
  value,
  currency = 'USD',
  num_items,
  user,
}) => Pixel('Purchase', {
  content_ids,
  order_id,
  content_type,
  contents,
  value,
  currency,
  num_items,
  user,
}, { eventID: order_id })

type CompleteRegistrationPayload = CoversionAPIPayload<{
  content_name: string,
  currency?: string,
  status?: boolean,
  value?: number,
  user_email? : string
}>

type CompleteRegistrationHandler = (
  item: CompleteRegistrationPayload
) => Promise<{ success: boolean }> | { success: boolean }
const completeRegistration: CompleteRegistrationHandler = ({
  content_name,
  status = true,
  currency = 'USD',
  user_email,
}) => Pixel('CompleteRegistration', {
  content_name,
  status,
  currency,
  user_email,
})

type SearchPayload = CoversionAPIPayload<{
  search_string: string,
  content_ids: FacebookPixelContentIds,
  contents: FacebookPixelContents,
  currency?: string,
  value?: number,
}>

type SearchHandler = (item: SearchPayload) => Promise<{ success: boolean }> | { success: boolean }
const search: SearchHandler = ({
  search_string,
  content_ids,
  contents,
  currency = 'USD',
  value = 0,
  user,
}) => Pixel('Search', {
  content_category: 'Product Search',
  content_ids,
  contents,
  currency,
  search_string,
  value,
  user,
})

type PixelHandler = () => Promise<{ success: boolean }>
const customizeProduct: PixelHandler = () => Pixel('CustomizeProduct')

const pageView: PixelHandler = () => Pixel('PageView')

const init = () => {
  try {
    debug(`Facebook: Pixel init typeof fbq = ${typeof window.fbq}`, { FACEBOOK_PIXEL_ID })
    window.fbq?.('init', FACEBOOK_PIXEL_ID)
    pageView()
  } catch (err) {
    error(`Facebook: Failed to Initialize Facebook Pixel ${String(err)}`, err)
  }
}

type FacebookAddToWishlistPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number
}>

type AddToWishlistHandler = (
  item: FacebookAddToWishlistPayload
) => Promise<{ success: boolean }> | { success: boolean }
const addToWishlist: AddToWishlistHandler = ({
  content_ids,
  content_category,
  content_name,
  content_type = 'product',
  currency = 'USD',
  contents,
  value,
  user,
}) => Pixel('AddToWishlist', {
  content_ids,
  content_category,
  content_name,
  content_type,
  contents,
  currency,
  value,
  user,
})

export const Facebook = {
  customizeProduct,
  search,
  completeRegistration,
  trackPurchase,
  initializeCheckout,
  viewContent,
  addToCart,
  pageView,
  init,
  hashing,
  parseUserData,
  addToWishlist,
}

export default Facebook
